import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper, Avatar } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import MDBox from 'components/MDBox';
import {
  useBulkDeleteAiProvidersMutation,
  useCreateAiProviderMutation,
  useGetAiProvidersQuery,
  useUpdateAiProviderMutation
} from 'services/aiProviderApi';
import { buildImageUrl, useUploadImageMutation } from 'services/imageResourceApi';
import './aiProvider.css';
import AiProviderDetails from 'pages/aiProviders/components/AiProviderDetails';
import AiProviderCustomToolbar from 'pages/aiProviders/components/AiProviderCustomToolbar';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';

/**
 * Component for managing AI Providers, including displaying a list of providers, adding, updating,
 * duplicating, and deleting providers. Uses a DataGrid for listing the providers and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <AiProviders />
 *
 * @returns {React.Component} The AI Providers management component.
 */
export default function AiProviders() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const apiRef = useGridApiRef();

  const {
    data: providers = [],
    error: providersError,
    isLoading: providersLoading
  } = useGetAiProvidersQuery();
  const [bulkDeleteAiProvider] = useBulkDeleteAiProvidersMutation();
  const [createAiProvider] = useCreateAiProviderMutation();
  const [updateAiProvider] = useUpdateAiProviderMutation();
  const [uploadImage] = useUploadImageMutation();

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.aiProviders.table.column.id` }),
      disableColumnMenu: true,
      minWidth: 50,
      maxWidth: 50
    },
    {
      field: 'icon',
      headerName: msg({ id: `page.aiProviders.table.column.icon` }),
      minWidth: 70,
      maxWidth: 70,
      display: 'flex',
      alignItems: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        // Use the provider's code as the image code
        const imageUrl = buildImageUrl(params.row.code, 'aiproviders');
        return (
          <Avatar src={imageUrl} alt={params.row.code} sx={{ width: '50px' }} variant="rounded">
            {params.row.code.charAt(0).toUpperCase()}
          </Avatar>
        );
      }
    },
    {
      field: 'code',
      headerName: msg({ id: `page.aiProviders.table.column.code` }),
      minWidth: 150,
      maxWidth: 250
    },
    {
      field: 'label',
      headerName: msg({ id: `page.aiProviders.table.column.label` }),
      minWidth: 150,
      maxWidth: 350
    },
    {
      field: 'description',
      headerName: msg({ id: `page.aiProviders.table.column.description` }),
      minWidth: 350,
      flex: 1
    },
    {
      field: 'url',
      headerName: msg({ id: `page.aiProviders.table.column.url` }),
      minWidth: 300
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedProvider(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedProvider(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteAiProvider(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.aiProviders.delete.success' })
          });
          clearProviderSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.aiProviders.delete.error' })
          });
        });
    }
  };

  const handleDuplicate = () => {
    const toDuplicate = selectedRows[0];
    if (toDuplicate) {
      setSelectedProvider({ ...toDuplicate, id: null, code: `${toDuplicate.code}_COPY` });
      setDetailDrawerOpen(true);
    }
  };

  // Modified form submit handler to handle SVG icon upload
  const handleFormSubmit = async (data) => {
    const { svgIconFile, ...providerData } = data;
    const newProvider = { ...providerData, apiKey: providerData.displayableKey };

    try {
      if (svgIconFile) {
        await uploadProviderIcon(newProvider.code, svgIconFile);
      }

      if (!newProvider.id) {
        await handleCreate(newProvider);
      } else {
        await handleUpdate(newProvider);
      }

      setDetailDrawerOpen(false);
    } catch (error) {
      console.error('Error saving provider or uploading icon:', error);
    }
  };

  const uploadProviderIcon = async (providerCode, svgFile) => {
    try {
      await uploadImage({
        file: svgFile,
        category: 'aiproviders',
        imageName: providerCode
      }).unwrap();

      addNotification({
        type: 'success',
        title: msg({ id: 'notification.success.title' }),
        message: msg({ id: 'page.aiProviders.icon.upload.success' })
      });
    } catch (error) {
      console.error('Error uploading icon:', error);
      addNotification({
        type: 'warning',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.aiProviders.icon.upload.error' })
      });
      throw error;
    }
  };

  const selectedRows = rowSelectionModel.map((id) =>
    providers.find((provider) => provider.id === id)
  );

  if (providersError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.aiProviders.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            apiRef={apiRef}
            rows={providers}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: providers && providers.length > 0 ? [providers[0].id] : [] },
              density: 'comfortable',
              sorting: {
                sortModel: [{ field: 'code', sort: 'desc' }]
              }
            }}
            slots={{ toolbar: AiProviderCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                onDuplicate: handleDuplicate,
                selectedRows
              }
            }}
            loading={providersLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <AiProviderDetails
            provider={selectedProvider}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearProviderSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedProvider(null);
    setSelectedRowId(null);
  }

  // Modified to return the created provider data
  function handleCreate(data) {
    return createAiProvider({ ...data })
      .unwrap()
      .then((result) => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.aiProviders.create.success' })
        });
        clearProviderSelected();
        return result;
      })
      .catch((error) => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.aiProviders.create.error' })
        });
        throw error;
      });
  }

  function handleUpdate(data) {
    return updateAiProvider({
      id: data.id,
      aiProviderRequest: { ...data }
    })
      .unwrap()
      .then((result) => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.aiProviders.update.success' })
        });
        clearProviderSelected();
        return result || data;
      })
      .catch((error) => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.aiProviders.update.error' })
        });
        throw error;
      });
  }
}
